import { useState, useEffect, useRef } from "react";
import { fmt } from "./Utils/Utilities";

const API_BASE = "https://api.seritonex.com";
const userId = () => Number(sessionStorage.getItem("userId"));
const token = () => localStorage.getItem("token");

// ─── Helpers ───────────────────────────────────────────────────────────────────

const toArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object") return [val];
  }
  if (data && typeof data === "object") return [data];
  return [];
};

// ─── API Endpoints ─────────────────────────────────────────────────────────────

const createorder = async (orderData) => {
  const response = await fetch(
    `${API_BASE}/api/v1/amis/purchase-orders/create`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token()}`,
      },
      body: JSON.stringify(orderData),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to create order: ${response.statusText}`,
    );
  }
  return await response.json();
};

const getWarehouseById = async (warehouse_id) => {
  const response = await fetch(`${API_BASE}/api/v1/amis/warehouse/get-by-id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify({ warehouse_id }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to fetch warehouse: ${response.statusText}`,
    );
  }
  return await response.json();
};

const getAssets = async (organization_id) => {
  const response = await fetch(`${API_BASE}/api/v1/amis/asset/types`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify({ organization_id }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to fetch assets: ${response.statusText}`,
    );
  }
  return await response.json();
};

const Cancelorder = async (purchase_order_id) => {
  const response = await fetch(
    `${API_BASE}/api/v1/amis/purchase-orders/cancel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token()}`,
      },
      body: JSON.stringify({ purchase_order_id }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to cancel order: ${response.statusText}`,
    );
  }
  return await response.json();
};

const getBranchCustomers = async (userId) => {
  const response = await fetch(`${API_BASE}/api/v1/amis/branch/by-user-id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message ||
        `Failed to fetch customer branches: ${response.statusText}`,
    );
  }
  return await response.json();
};

const getBranchSuppliers = async (branch_id) => {
  const response = await fetch(
    `${API_BASE}/api/v1/amis/branch/branch-suppliers`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token()}`,
      },
      body: JSON.stringify({ branch_id }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message ||
        `Failed to fetch supplier branches: ${response.statusText}`,
    );
  }
  return await response.json();
};

const getAllWarehouses = async (branch_id) => {
  const response = await fetch(
    `${API_BASE}/api/v1/amis/warehouse/get-by-branch-id`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token()}`,
      },
      body: JSON.stringify({ branch_id }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || `Failed to fetch warehouses: ${response.statusText}`,
    );
  }
  return await response.json();
};

// ─── Step config ───────────────────────────────────────────────────────────────

const STEPS = [
  {
    id: 0,
    label: "New Order",
    desc: "Select gas products, quantities, and delivery dates",
  },
  {
    id: 1,
    label: "Review & Submit",
    desc: "Confirm your order details before submitting",
  },
];

// ─── Asset normalisation ───────────────────────────────────────────────────────

const normalizeAsset = (a) => {
  const props = a.asset_type_properties || [];
  // property_id 1 is the "capacity pallet" link — whatever asset type it
  // points to (via linked_asset_type) IS this asset's pallet container.
  // Which pallet type that is DIFFERS per asset (e.g. OXYGEN links to
  // GC-PALLET, while ACETYLENE/CO2/ARGON/FLUXBLEND/NITROGEN link to
  // G4-Rental PALLET) — so it's always read dynamically off the data here,
  // never hardcoded to a single fixed code.
  const palletProp = props.find(
    (p) => p.property_id === 1 && p.linked_asset_type?.asset_type_id != null,
  );
  const hasPallet = !!palletProp;
  const cpp = hasPallet ? Number(palletProp.prop_value) || null : null;
  return {
    asset_id: a.asset_type_id,
    asset_type_id: a.asset_type_id,
    type_code: a.type_code ?? "",
    description: a.description ?? "",
    asset_type_properties: props,
    has_pallet: hasPallet,
    cylinders_per_pallet: cpp,
    pallet_asset_type_id: palletProp?.linked_asset_type?.asset_type_id ?? null,
    pallet_type_code: palletProp?.linked_asset_type?.type_code ?? null,
    pallet_description: palletProp?.linked_asset_type?.description ?? null,
    default_qty: "",
  };
};

// ─── Line model ────────────────────────────────────────────────────────────────

const EMPTY_LINE = () => ({
  asset_id: "",
  asset_type_id: "",
  asset_type_code: "",
  product: "",
  pallets: "",
  qty: "",
  has_pallet: false,
  cylinders_per_pallet: null,
  pallet_asset_type_id: null,
  pallet_type_code: null,
  pallet_description: null,
  properties: [],
});

const EMPTY_FORM = {
  customer_branch_id: "",
  supplier_branch_id: "",
  warehouse_id: "",
  purchase_order_number: "",
  delivery_date: "",
  delivery_address: "",
  notes: "",
  _organization_id: null,
  _warehouseAddress: null,
  _customerBranchName: "",
  _supplierBranchName: "",
  _warehouseName: "",
  _branch_customer_id: null,
  lines: [{ ...EMPTY_LINE() }],
};

// ─── Pallet group computation ───────────────────────────────────────────────
// Replaces the old single hardcoded "PAL-A" concept. Each order line that
// carries a pallet links (via property_id 1 on its asset type) to some
// pallet container asset type — and that container can differ from line to
// line. This groups the currently selected lines by pallet_asset_type_id and
// sums their pallets/qty per group, so every distinct pallet type in play
// gets its own auto-computed summary row instead of assuming there's only
// ever one universal pallet. Per-line pallet math (rounding, etc.) is
// unchanged — this only changes how the totals get bucketed and labeled.
const computePalletGroups = (lines) => {
  const map = new Map();
  for (const l of lines) {
    if (!l.asset_id || !l.has_pallet || l.pallet_asset_type_id == null)
      continue;
    const key = Number(l.pallet_asset_type_id);
    const group =
      map.get(key) ??
      {
        pallet_asset_type_id: key,
        type_code: l.pallet_type_code || "",
        description: l.pallet_description || "",
        pallets: 0,
        qty: 0,
      };
    group.pallets += Number(l.pallets) || 0;
    group.qty += Number(l.qty) || 0;
    if (!group.type_code && l.pallet_type_code) group.type_code = l.pallet_type_code;
    if (!group.description && l.pallet_description)
      group.description = l.pallet_description;
    map.set(key, group);
  }
  return Array.from(map.values());
};

// ─── Reusable small components ─────────────────────────────────────────────────

function Field({ label, children, hint, style }) {
  return (
    <div className="fg" style={style}>
      <label className="fl">{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function StepHeading({ step }) {
  const s = STEPS[step];
  return (
    <div
      style={{
        marginBottom: 20,
        paddingBottom: 14,
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 2,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)" }}>
          Step {step + 1} of {STEPS.length} — {s.label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)" }}>{s.desc}</div>
    </div>
  );
}

function SelectStatus({ loading, error, count }) {
  if (loading)
    return (
      <div
        style={{
          fontSize: 10,
          color: "var(--amber)",
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <span
          className="spin"
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            border: "2px solid var(--amber)",
            borderTopColor: "transparent",
            borderRadius: "50%",
          }}
        />
        Loading…
      </div>
    );
  if (error)
    return (
      <div style={{ fontSize: 10, color: "var(--danger)", marginTop: 4 }}>
        ⚠ {error}
      </div>
    );
  if (count === 0)
    return (
      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>
        No results found
      </div>
    );
  return null;
}

// ─── Step 0: Order Builder ─────────────────────────────────────────────────────

function StepOrderBuilder({
  form,
  setForm,
  customers,
  setCustomers,
  custLoading,
  setCustLoading,
  custError,
  setCustError,
  suppliers,
  setSuppliers,
  suppLoading,
  setSuppLoading,
  suppError,
  setSuppError,
  warehouses,
  setWarehouses,
  whLoading,
  setWhLoading,
  whError,
  setWhError,
  assets,
  setAssets,
  assetsLoading,
  setAssetsLoading,
  assetsError,
  setAssetsError,
  assetsFetchId,
}) {
  useEffect(() => {
    if (customers.length > 0 || custError) return;
    if (!userId()) {
      setCustError("No user session found. Please log in again.");
      setCustLoading(false);
      return;
    }
    getBranchCustomers(userId())
      .then((data) => setCustomers(toArray(data, "branches", "data", "items")))
      .catch((e) => setCustError(e.message))
      .finally(() => setCustLoading(false));
  }, []);

  const optLabel = (item) =>
    item.name ??
    item.branch_name ??
    item.supplier_name ??
    item.customer_name ??
    item.warehouse_name ??
    item.label ??
    `ID ${item.id ?? item.branch_id ?? item.warehouse_id ?? "?"}`;

  const optValue = (item) =>
    item.id ??
    item.branch_id ??
    item.supplier_id ??
    item.customer_id ??
    item.warehouse_id ??
    "";

  // ── Cascade handlers ──────────────────────────────────────────────────────

  function handleCustomerBranchChange(branchId) {
    const selected = customers.find(
      (c) => String(optValue(c)) === String(branchId),
    );
    setForm((f) => ({
      ...f,
      customer_branch_id: branchId,
      _customerBranchName: selected ? optLabel(selected) : "",
      _organization_id: selected?.organization?.organization_id ?? null,
      supplier_branch_id: "",
      _supplierBranchName: "",
      warehouse_id: "",
      _warehouseName: "",
      delivery_address: "",
      _warehouseAddress: null,
      lines: [{ ...EMPTY_LINE() }],
    }));
    setSuppliers([]);
    setWarehouses([]);
    setAssets([]);
    setSuppError(null);
    setWhError(null);
    setAssetsError(null);
    if (!branchId) return;
    setSuppLoading(true);
    getBranchSuppliers(Number(branchId))
      .then((data) =>
        setSuppliers(
          toArray(data, "customers", "suppliers", "branches", "data", "items"),
        ),
      )
      .catch((e) => setSuppError(e.message))
      .finally(() => setSuppLoading(false));
  }

  function handleSupplierBranchChange(supplierBranchId) {
    const selected = suppliers.find(
      (s) => String(s.branch_id) === String(supplierBranchId),
    );
    setForm((f) => ({
      ...f,
      supplier_branch_id: supplierBranchId,
      _supplierBranchName: selected ? optLabel(selected) : "",
      _branch_customer_id: selected?.branch_customer_id ?? null,
      warehouse_id: "",
      _warehouseName: "",
      delivery_address: "",
      _warehouseAddress: null,
      lines: [{ ...EMPTY_LINE() }],
    }));
    setWarehouses([]);
    setAssets([]);
    setWhError(null);
    setAssetsError(null);
    if (!supplierBranchId) return;
    if (!form.customer_branch_id) {
      setWhError("No customer branch selected.");
      return;
    }

    setWhLoading(true);
    getAllWarehouses(Number(form.customer_branch_id))
      .then((data) =>
        setWarehouses(toArray(data, "warehouses", "data", "items")),
      )
      .catch((e) => setWhError(e.message))
      .finally(() => setWhLoading(false));

    const supplierOrgId = selected?.organization?.organization_id ?? null;
    if (supplierOrgId) {
      const fetchId = ++assetsFetchId.current;
      setAssetsLoading(true);
      setAssetsError(null);
      getAssets(Number(supplierOrgId))
        .then((data) => {
          if (fetchId !== assetsFetchId.current) return;
          const raw = toArray(data, "assetTypes", "assets", "data", "items");
          const normalized = raw.map(normalizeAsset);
          setAssets(normalized);
        })
        .catch((e) => {
          if (fetchId !== assetsFetchId.current) return;
          setAssetsError(e.message);
        })
        .finally(() => {
          if (fetchId === assetsFetchId.current) setAssetsLoading(false);
        });
    } else {
      setAssetsError("Supplier has no organization — cannot load asset types.");
    }
  }

  async function handleWarehouseChange(warehouseId) {
    const selected = warehouses.find(
      (w) => String(optValue(w)) === String(warehouseId),
    );
    setForm((f) => ({
      ...f,
      warehouse_id: warehouseId,
      _warehouseName: selected ? optLabel(selected) : "",
      delivery_address: "",
      _warehouseAddress: null,
    }));
    if (!warehouseId) return;
    try {
      const data = await getWarehouseById(Number(warehouseId));
      const addr = data?.warehouse?.address ?? {};
      setForm((f) => ({
        ...f,
        delivery_address: [
          addr.address_line_1,
          addr.address_line_2,
          addr.city,
          addr.region,
          addr.postal_code,
          addr.country,
        ]
          .filter(Boolean)
          .join(", "),
        _warehouseAddress: {
          address_id: addr.address_id ?? null,
          address_line_1: addr.address_line_1 ?? "",
          address_line_2: addr.address_line_2 ?? "",
          city: addr.city ?? "",
          region: addr.region ?? "",
          postal_code: addr.postal_code ?? "",
          country: addr.country ?? "",
          geo_location: addr.geo_location ?? "",
        },
      }));
    } catch (e) {
      setWhError(e.message);
    }
  }

  // ── Line helpers ──────────────────────────────────────────────────────────

  const updateLine = (idx, patch) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));

  const applyAssetToLine = (idx, picked) => {
    updateLine(idx, {
      asset_id: picked.asset_id,
      asset_type_id: picked.asset_type_id,
      asset_type_code: picked.type_code,
      product: picked.description,
      properties: picked.asset_type_properties,
      has_pallet: picked.has_pallet,
      cylinders_per_pallet: picked.cylinders_per_pallet,
      pallet_asset_type_id: picked.pallet_asset_type_id,
      pallet_type_code: picked.pallet_type_code,
      pallet_description: picked.pallet_description,
      pallets: "",
      qty: "",
    });
  };

  const clearLine = (idx) => updateLine(idx, { ...EMPTY_LINE() });
  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE() }] }));

  // Pallets → Qty (forward calc): typing a pallet count computes the exact
  // cylinder qty (pallets * cylinders_per_pallet). Fractional pallet values
  // are preserved as-is, no rounding.
  const handlePalletsChange = (idx, rawVal) => {
    const cpp = Number(form.lines[idx].cylinders_per_pallet) || 1;
    const pallets = Number(rawVal);
    updateLine(idx, {
      pallets: rawVal,
      qty: pallets > 0 ? String(pallets * cpp) : "",
    });
  };

  // Qty → Pallets (reverse calc): typing a cylinder qty derives the pallet
  // count needed to cover it, ALWAYS rounded UP (never down), since you can't
  // ship a partial pallet short of what was requested. The qty the user typed
  // is kept exactly as entered — it is NOT bumped up to a full pallet's worth.
  // e.g. cylinders_per_pallet = 16, qty = 34 -> pallets = ceil(34/16) = 3, qty stays 34.
  const handleQtyChange = (idx, rawVal) => {
    const line = form.lines[idx];
    const cpp = Number(line.cylinders_per_pallet) || 1;
    const qty = Number(rawVal);

    if (line.has_pallet && rawVal !== "" && qty > 0) {
      const pallets = Math.ceil(qty / cpp);
      updateLine(idx, { qty: rawVal, pallets: String(pallets) });

      // ─── ALTERNATE BEHAVIOR (do NOT delete — keep in case manager wants it) ───
      // If the business decides the entered qty itself should snap UP to match
      // full pallet capacity (e.g. 34 -> 48 for cpp=16), instead of leaving the
      // qty exactly as typed, DELETE the `updateLine` call directly above this
      // comment block and UNCOMMENT the line below instead:
      //
      // updateLine(idx, { qty: String(pallets * cpp), pallets: String(pallets) });
      // ───────────────────────────────────────────────────────────────────────
    } else if (line.has_pallet && (rawVal === "" || qty <= 0)) {
      // Clearing/zeroing qty on a pallet-bearing line clears the derived
      // pallet count too, so stale pallet numbers don't linger.
      updateLine(idx, { qty: rawVal, pallets: "" });
    } else {
      // Non-pallet asset: qty is a direct entry, no pallet math involved.
      // Pallets is force-cleared/kept blank so a non-pallet asset can never
      // contribute a pallet count to anything downstream.
      updateLine(idx, { qty: rawVal, pallets: "" });
    }
  };

  // ── Pallet container types: excluded from selection, always auto-computed ──
  // Any asset that shows up as the pallet link (property_id 1) target for
  // some other asset is itself a pallet container — never something a user
  // picks as a regular order line. It can only ever appear as an
  // auto-computed summary row (see palletGroups below). This is fully
  // dynamic: whatever type_code/description the data links to is what gets
  // excluded and shown — nothing is hardcoded to a single "PAL-A" code.
  const palletContainerIds = new Set(
    assets
      .map((a) => a.pallet_asset_type_id)
      .filter((id) => id != null)
      .map(Number),
  );

  // Assets the user can actually pick (pallet containers excluded)
  const selectableAssets = assets.filter(
    (a) => !palletContainerIds.has(Number(a.asset_type_id)),
  );

  // One group per distinct pallet asset type actually in use on the current
  // lines. Empty array = nothing to show — same effect as the old
  // anyPalletLineSelected check, just generalized to N pallet types.
  const palletGroups = computePalletGroups(form.lines);

  // ── Deduplication (selectable assets only) ────────────────────────────────

  const assetsReady = !assetsLoading && selectableAssets.length > 0;
  const usedAssets = new Set(
    form.lines.filter((l) => l.asset_id).map((l) => String(l.asset_id)),
  );
  const availableForLine = (lineIdx) => {
    const own = String(form.lines[lineIdx].asset_id);
    return selectableAssets.filter(
      (a) => !usedAssets.has(String(a.asset_id)) || String(a.asset_id) === own,
    );
  };
  const remainingAssets = selectableAssets.filter(
    (a) => !usedAssets.has(String(a.asset_id)),
  ).length;
  const canAddNewLine = assetsReady && remainingAssets > 0;

  // ── Column layout ─────────────────────────────────────────────────────────

  const GRID = "1fr 1fr 110px 130px 36px";

  const colHdr = (label, align = "left") => (
    <div
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        color: "var(--text1)",
        textAlign: align,
      }}
    >
      {label}
    </div>
  );

  // ── Asset dropdown pair ───────────────────────────────────────────────────

  const renderAssetSelects = (line, availableAssets, onPick, onClear) => (
    <>
      <select
        className="fsel"
        value={line.asset_type_code}
        disabled={assetsLoading}
        onChange={(e) => {
          const p = selectableAssets.find(
            (a) => a.type_code === e.target.value,
          );
          p ? onPick(p) : onClear();
        }}
      >
        <option value="">— Code —</option>
        {availableAssets.map((a) => (
          <option key={a.asset_id} value={a.type_code}>
            {a.type_code}
          </option>
        ))}
      </select>
      <select
        className="fsel"
        value={line.asset_id}
        disabled={assetsLoading}
        onChange={(e) => {
          const p = selectableAssets.find(
            (a) => String(a.asset_id) === String(e.target.value),
          );
          p ? onPick(p) : onClear();
        }}
      >
        <option value="">— Description —</option>
        {availableAssets.map((a) => (
          <option key={a.asset_id} value={a.asset_id}>
            {a.description}
          </option>
        ))}
      </select>
    </>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ animation: "slup .18s ease" }}>
      <StepHeading step={0} />

      {/* ── Branch / Warehouse selectors ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Field label="Customer Branch">
          <select
            className="fsel"
            value={form.customer_branch_id}
            disabled={custLoading}
            onChange={(e) => handleCustomerBranchChange(e.target.value)}
          >
            <option value="">— Select customer branch —</option>
            {customers.map((c) => (
              <option key={optValue(c)} value={optValue(c)}>
                {optLabel(c)}
              </option>
            ))}
          </select>
          <SelectStatus
            loading={custLoading}
            error={custError}
            count={customers.length}
          />
        </Field>

        <Field label="Supplier Branch">
          <select
            className="fsel"
            value={form.supplier_branch_id}
            disabled={!form.customer_branch_id || suppLoading}
            onChange={(e) => handleSupplierBranchChange(e.target.value)}
          >
            <option value="">
              {!form.customer_branch_id
                ? "— Select customer branch first —"
                : "— Select supplier branch —"}
            </option>
            {suppliers.map((s) => (
              <option key={s.branch_id} value={s.branch_id}>
                {optLabel(s)}
              </option>
            ))}
          </select>
          <SelectStatus
            loading={suppLoading}
            error={suppError}
            count={suppliers.length}
          />
        </Field>

        <Field label="Warehouse">
          <select
            className="fsel"
            value={form.warehouse_id}
            disabled={!form.supplier_branch_id || whLoading}
            onChange={(e) => handleWarehouseChange(e.target.value)}
          >
            <option value="">
              {!form.supplier_branch_id
                ? "— Select supplier branch first —"
                : "— Select warehouse —"}
            </option>
            {warehouses.map((w) => (
              <option key={optValue(w)} value={optValue(w)}>
                {optLabel(w)}
              </option>
            ))}
          </select>
          <SelectStatus
            loading={whLoading}
            error={whError}
            count={warehouses.length}
          />
        </Field>

        <Field label="Purchase Order Number">
          <input
            className="fi"
            type="text"
            placeholder="e.g. PO-12345"
            value={form.purchase_order_number}
            onChange={(e) =>
              setForm((f) => ({ ...f, purchase_order_number: e.target.value }))
            }
          />
        </Field>

        <Field label="Latest Delivery Date">
          <input
            className="fi"
            type="date"
            value={form.delivery_date}
            onChange={(e) =>
              setForm((f) => ({ ...f, delivery_date: e.target.value }))
            }
          />
        </Field>
      </div>

      {/* ── Delivery Address ── */}
      {form.warehouse_id && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "var(--r8)",
            border: "1px solid var(--bright)",
            background: "var(--deep)",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: "var(--text3)",
              marginBottom: 6,
            }}
          >
            Delivery Address
          </div>
          <div
            style={{
              fontSize: 12,
              color: form.delivery_address ? "var(--text1)" : "var(--text3)",
              lineHeight: 1.6,
            }}
          >
            {form.delivery_address ||
              "No address available for selected warehouse"}
          </div>
        </div>
      )}

      {/* ── Assets loading indicator ── */}
      {assetsLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            color: "var(--amber)",
            marginBottom: 10,
          }}
        >
          <span
            className="spin"
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              border: "2px solid var(--amber)",
              borderTopColor: "transparent",
              borderRadius: "50%",
            }}
          />
          Loading asset types…
        </div>
      )}

      {/* ── Order Lines label ── */}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--text3)",
          marginBottom: 10,
        }}
      >
        Order Lines
        {assetsReady && (
          <span
            style={{
              marginLeft: 8,
              fontWeight: 400,
              textTransform: "none",
              fontSize: 10,
            }}
          >
            ({selectableAssets.length} asset type
            {selectableAssets.length !== 1 ? "s" : ""} available)
          </span>
        )}
      </div>

      <div
        style={{
          borderRadius: "var(--r8)",
          border: "1px solid var(--bright)",
          background: "var(--deep)",
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GRID,
            gap: 10,
            padding: "8px 14px",
            borderBottom: "1px solid var(--bright)",
            background: "var(--surface)",
          }}
        >
          {colHdr("Code")}
          {colHdr("Description")}
          {colHdr("Pallets", "right")}
          {colHdr("Qty (Cylinders)", "right")}
          {colHdr("")}
        </div>

        {/* ── User-editable lines ── pallet container types are never an option here ── */}
        {form.lines.map((line, idx) => {
          const available = availableForLine(idx);
          const cpp = Number(line.cylinders_per_pallet) || 1;
          const hasPallet = line.has_pallet;
          const palletsNum = Number(line.pallets) || 0;
          const qtyNum = Number(line.qty) || 0;

          return (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                gap: 10,
                padding: "10px 14px",
                alignItems: "start",
                borderBottom:
                  idx < form.lines.length - 1
                    ? "1px solid var(--border)"
                    : "none",
              }}
            >
              {renderAssetSelects(
                line,
                available,
                (picked) => applyAssetToLine(idx, picked),
                () => clearLine(idx),
              )}

              {/* ── Pallets column ── */}
              <div>
                <input
                  className="fi"
                  type="number"
                  min={0}
                  placeholder={hasPallet ? "0" : "—"}
                  value={hasPallet ? line.pallets : ""}
                  enabled={!line.asset_id || !hasPallet}
                  onChange={(e) => handlePalletsChange(idx, e.target.value)}
                  style={{
                    textAlign: "right",
                    opacity: !line.asset_id || !hasPallet ? 0.38 : 1,
                    cursor:
                      !line.asset_id || !hasPallet ? "not-allowed" : "text",
                  }}
                />
                {line.asset_id && hasPallet && palletsNum > 0 && (
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--text3)",
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    {palletsNum} × {cpp} cyl
                  </div>
                )}
                {line.asset_id && hasPallet && !palletsNum && (
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--text3)",
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    {cpp} cyl/pallet
                  </div>
                )}
                {line.asset_id && !hasPallet && (
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--text3)",
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    no pallet
                  </div>
                )}
              </div>

              {/* ── Qty (Cylinders) column ── */}
              <div>
                <input
                  className="fi"
                  type="number"
                  min={0}
                  placeholder="0"
                  value={line.qty}
                  onChange={(e) => handleQtyChange(idx, e.target.value)}
                  style={{
                    textAlign: "right",
                    opacity: !line.asset_id ? 0.55 : 1,
                    cursor: !line.asset_id ? "not-allowed" : "text",
                  }}
                />
                {line.asset_id && hasPallet && qtyNum > 0 && (
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--text3)",
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    → {palletsNum} pallet{palletsNum !== 1 ? "s" : ""} (rounded
                    up)
                  </div>
                )}
                {line.asset_id && !hasPallet && (
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--text3)",
                      marginTop: 3,
                      textAlign: "right",
                    }}
                  >
                    direct qty
                  </div>
                )}
              </div>

              {/* ── Remove ── */}
              <button
                onClick={() =>
                  setForm((f) => {
                    const filtered = f.lines.filter((_, i) => i !== idx);
                    return {
                      ...f,
                      lines:
                        filtered.length > 0 ? filtered : [{ ...EMPTY_LINE() }],
                    };
                  })
                }
                disabled={form.lines.length === 1 && !line.asset_id}
                title="Remove line"
                style={{
                  height: 32,
                  width: 32,
                  borderRadius: 6,
                  border: "1px solid var(--bright)",
                  cursor: "pointer",
                  background: "var(--elevated)",
                  color: "var(--danger)",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: form.lines.length === 1 && !line.asset_id ? 0.35 : 1,
                  transition: "all .15s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(220,53,69,.1)";
                  e.currentTarget.style.borderColor = "var(--danger,#e05260)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--elevated)";
                  e.currentTarget.style.borderColor = "var(--bright)";
                }}
              >
                🗑
              </button>
            </div>
          );
        })}

        {/* ── Auto-Computed Pallet Summary Rows ──
             One row per distinct pallet asset type, derived dynamically from
             property_id 1 on each selected asset (see computePalletGroups).
             Replaces the old hardcoded single "PAL-A" row — different
             assets can link to different pallet asset types (e.g.
             GC-PALLET vs G4-Rental PALLET), and each gets its own auto row
             here. A row only appears once a real pallet-bearing line linked
             to that pallet type has been selected. ── */}
        {palletGroups.map((group) => (
          <div
            key={group.pallet_asset_type_id}
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              gap: 10,
              padding: "10px 14px",
              alignItems: "start",
              borderTop: "1px solid var(--border)",
              background: "transparent",
            }}
          >
            {/* Code badge */}
            <div
              style={{
                height: 37,
                display: "flex",
                alignItems: "center",
                background: "var(--void)",
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid var(--bright)",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text2)",
                  letterSpacing: ".05em",
                }}
              >
                {group.type_code || "PALLET"}
              </span>
            </div>

            {/* Description + AUTO badge */}
            <div
              style={{
                height: 37,
                display: "flex",
                alignItems: "center",
                background: "var(--void)",
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid var(--bright)",
              }}
            >
              <span style={{ fontSize: 11, color: "var(--text2)" }}>
                {group.description || "Pallet"}
              </span>
              <span
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  color: "var(--text3)",
                  padding: "2px 5px",
                  borderRadius: 3,
                  background: "transparent",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                }}
              >
                AUTO
              </span>
            </div>

            {/* Total pallets */}
            <div>
              <input
                className="fi"
                type="number"
                value={group.pallets || ""}
                readOnly
                disabled
                placeholder="0"
                style={{
                  textAlign: "right",
                  opacity: 1,
                  cursor: "default",
                }}
              />
              <div
                style={{
                  fontSize: 9,
                  marginTop: 3,
                  textAlign: "right",
                  fontWeight: 600,
                  color: "var(--text3)",
                }}
              >
                {group.pallets > 0 ? `${group.pallets} total` : "no pallets yet"}
              </div>
            </div>

            {/* Total cylinders */}
            <div>
              <input
                className="fi"
                type="number"
                value={group.qty || ""}
                readOnly
                disabled
                placeholder="0"
                style={{
                  textAlign: "right",
                  opacity: 1,
                  cursor: "default",
                }}
              />
              <div
                style={{
                  fontSize: 9,
                  marginTop: 3,
                  textAlign: "right",
                  fontWeight: 600,
                  color: "var(--text3)",
                }}
              >
                {group.qty > 0 ? `${group.qty} cylinders` : "auto-computed"}
              </div>
            </div>

            {/* No remove button — auto pallet rows cannot be deleted */}
            <div />
          </div>
        ))}

        {/* ── Add Line ── */}
        <div
          style={{ padding: "8px 14px", borderTop: "1px solid var(--border)" }}
        >
          <button
            onClick={addLine}
            disabled={!canAddNewLine}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: "var(--elevated)",
              borderRadius: 6,
              border: `1px dashed ${canAddNewLine ? "var(--flame)" : "var(--bright)"}`,
              color: canAddNewLine ? "var(--text2)" : "var(--text1)",
              fontSize: 11,
              fontWeight: 600,
              cursor: canAddNewLine ? "pointer" : "not-allowed",
              transition: "all .15s",
              opacity: canAddNewLine ? 1 : 0.5,
            }}
          >
            + Add Line
            {assetsLoading && (
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                (loading assets…)
              </span>
            )}
            {!assetsLoading &&
              selectableAssets.length === 0 &&
              form.supplier_branch_id && (
                <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                  (no assets available)
                </span>
              )}
            {!assetsLoading && assetsReady && remainingAssets === 0 && (
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                (all assets selected)
              </span>
            )}
            {!form.supplier_branch_id && (
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                (select supplier branch first)
              </span>
            )}
          </button>
          {assetsError && (
            <div style={{ fontSize: 10, color: "var(--danger)", marginTop: 4 }}>
              ⚠ {assetsError}
            </div>
          )}
        </div>
      </div>

      <Field label="Notes (optional)">
        <textarea
          className="fi"
          rows={3}
          placeholder="Any special instructions..."
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          style={{ resize: "vertical" }}
        />
      </Field>
    </div>
  );
}

// ─── Step 1: Review & Confirm ──────────────────────────────────────────────────

function StepReviewConfirm({ form, submitting, submitError, palletGroups }) {
  const deliveryDisplay = (() => {
    if (!form.delivery_date) return null;
    try {
      const [y, m, d] = form.delivery_date.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return form.delivery_date;
    }
  })();

  const orderLines = form.lines.filter((l) => l.asset_id);

  const headerRows = [
    ...(form.purchase_order_number
      ? [{ label: "PO Number", value: form.purchase_order_number }]
      : []),
    ...(deliveryDisplay
      ? [{ label: "Latest Delivery", value: deliveryDisplay }]
      : []),
    {
      label: "Customer Branch",
      value: form._customerBranchName || form.customer_branch_id || "—",
    },
    {
      label: "Supplier Branch",
      value: form._supplierBranchName || form.supplier_branch_id || "—",
    },
    {
      label: "Warehouse",
      value: form._warehouseName || form.warehouse_id || "—",
    },
    { label: "Delivery Address", value: form.delivery_address || "—" },
  ];

  // ── Asset rows: 3-column layout (Name | Code | Qty) ──────────────────────
  const assetRows = orderLines.map((l) => {
    const cpp = Number(l.cylinders_per_pallet) || 0;
    const qty = Number(l.qty) || 0;
    const pallets = Number(l.pallets) || 0;
    // NOTE: pallets * cpp is no longer guaranteed to equal qty, since qty can
    // now be entered directly and pallets rounded UP to cover it (e.g. 3
    // pallets @ 16 cyl covering a requested qty of 34, not 48). Label reflects
    // "requested" qty rather than implying pallets * cpp = qty.
    const qtyStr = l.has_pallet
      ? `${pallets} plt (${cpp}/plt) · ${qty} cyl requested`
      : `${qty} cylinder${qty !== 1 ? "s" : ""}`;
    return {
      name: l.product || "—",
      code: l.asset_type_code || "—",
      qty: qtyStr,
      isComputed: false,
    };
  });

  // Auto-computed pallet rows — one per distinct pallet asset type in use,
  // derived dynamically off property_id 1 rather than a fixed "PAL-A" code.
  for (const group of palletGroups) {
    if (group.pallets > 0) {
      assetRows.push({
        name: group.description || group.type_code || "Pallet",
        code: group.type_code || "—",
        qty: `${group.pallets} plt · ${group.qty} cyl`,
        isComputed: true,
      });
    }
  }

  return (
    <div style={{ animation: "slup .18s ease" }}>
      <StepHeading step={1} />

      {/* ── Order details ── */}
      <div
        style={{
          borderRadius: "var(--r8)",
          overflow: "hidden",
          border: "1px solid var(--bright)",
          marginBottom: 14,
        }}
      >
        {headerRows.map((r, i) => (
          <div
            key={r.label}
            className="fb"
            style={{
              padding: "10px 16px",
              borderBottom:
                i < headerRows.length - 1 ? "1px solid var(--border)" : "none",
              background: i % 2 === 0 ? "var(--deep)" : "var(--surface)",
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "var(--text3)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              {r.label}
            </span>
            <span
              style={{ fontSize: 12, fontWeight: 700, color: "var(--text1)" }}
            >
              {r.value}
            </span>
          </div>
        ))}
      </div>

      {/* ── Asset lines: Name (left) · Code (center) · Qty (right) ── */}
      {assetRows.length > 0 && (
        <div
          style={{
            borderRadius: "var(--r8)",
            overflow: "hidden",
            border: "1px solid var(--bright)",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 100px 140px",
              gap: 10,
              padding: "8px 16px",
              borderBottom: "1px solid var(--bright)",
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "var(--text1)",
                textAlign: "left",
              }}
            >
              Asset
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "var(--text1)",
                textAlign: "center",
              }}
            >
              Code
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "var(--text1)",
                textAlign: "right",
              }}
            >
              Qty
            </div>
          </div>

          {assetRows.map((r, i) => (
            <div
              key={`${r.code}-${i}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 100px 140px",
                gap: 10,
                padding: "10px 16px",
                alignItems: "center",
                borderBottom:
                  i < assetRows.length - 1 ? "1px solid var(--border)" : "none",
                background: r.isComputed
                  ? "rgba(var(--teal-rgb, 45,212,191),.05)"
                  : i % 2 === 0
                    ? "var(--deep)"
                    : "var(--surface)",
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: r.isComputed ? "var(--teal)" : "var(--text1)",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {r.name}
                {r.isComputed && (
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 700,
                      color: "var(--teal)",
                      padding: "2px 5px",
                      borderRadius: 3,
                      background: "var(--surface)",
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                    }}
                  >
                    AUTO
                  </span>
                )}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: r.isComputed ? "var(--teal)" : "var(--text2)",
                  textAlign: "center",
                }}
              >
                {r.code}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: r.isComputed ? "var(--teal)" : "var(--text1)",
                  textAlign: "right",
                }}
              >
                {r.qty}
              </span>
            </div>
          ))}
        </div>
      )}

      {form.notes && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: "var(--r8)",
            background: "var(--deep)",
            border: "1px solid var(--bright)",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: "var(--text3)",
              marginBottom: 5,
            }}
          >
            Notes
          </div>
          <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>
            {form.notes}
          </div>
        </div>
      )}

      {submitting && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: "var(--r8)",
            background: "rgba(255,79,23,.07)",
            border: "1px solid var(--flame)",
            fontSize: 12,
            color: "var(--flame)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 14 }}></span> Submitting your order…
        </div>
      )}
      {submitError && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: "var(--r8)",
            background: "rgba(220,53,69,.08)",
            border: "1px solid rgba(220,53,69,.4)",
            fontSize: 12,
            color: "#e05260",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 14 }}>⚠️</span> {submitError}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function CreateOrderPage({ showToast, setPage }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(true);
  const [custError, setCustError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [suppLoading, setSuppLoading] = useState(false);
  const [suppError, setSuppError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [whLoading, setWhLoading] = useState(false);
  const [whError, setWhError] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState(null);
  const assetsFetchId = useRef(0);

  // ── Pallet groups (shared between the builder, review, and confirm) ───────
  // See computePalletGroups — one entry per distinct pallet asset type
  // currently in use on the order, read dynamically off property_id 1 on
  // each asset rather than a hardcoded "PAL-A" code.
  const palletGroups = computePalletGroups(form.lines);

  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  function handleNext() {
    if (!isLast) setStep((s) => s + 1);
  }
  function handleBack() {
    if (!isFirst) setStep((s) => s - 1);
    else setPage("orders");
  }

  async function handleConfirm() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const addr = form._warehouseAddress || {};

      const details = form.lines
        .filter((l) => l.asset_id)
        .map((l) => {
          const cpp = Number(l.cylinders_per_pallet) || null;
          const qty = Number(l.qty) || 0;
          // Non-pallet lines always send 0 pallets — never left blank/undefined
          // and never inheriting the cylinder qty.
          const pallets = l.has_pallet ? Number(l.pallets) || 0 : 0;
          return {
            asset_type_id: l.asset_type_id || undefined,
            asset_type_code: l.asset_type_code || undefined,
            asset_type_description: l.product || undefined,
            qty_ordered: qty,
            qty_pallets: pallets,
            cylinders_per_pallet: cpp ?? undefined,
            pallet_asset_type_id: l.pallet_asset_type_id ?? undefined,
          };
        });

      // Append one auto-computed entry per pallet type actually in use.
      // Replaces the old single hardcoded PAL-A push — if no pallet lines
      // exist, palletGroups is empty and nothing extra gets sent.
      for (const group of palletGroups) {
        if (group.pallets > 0) {
          details.push({
            asset_type_id: group.pallet_asset_type_id,
            asset_type_code: group.type_code || undefined,
            asset_type_description: group.description || undefined,
            qty_ordered: group.pallets,
            qty_pallets: group.pallets,
          });
        }
      }

      const payload = {
        customer_branch_id: Number(form._branch_customer_id) || undefined,
        supplier_branch_id: Number(form.supplier_branch_id) || undefined,
        warehouse_id: Number(form.warehouse_id) || undefined,
        purchase_order_number: form.purchase_order_number || undefined,
        order_date: new Date().toISOString().split("T")[0],
        required_delivery_date: form.delivery_date || undefined,
        delivery_address: addr.address_id || undefined,
        address_line_1: addr.address_line_1 || undefined,
        address_line_2: addr.address_line_2 || undefined,
        city: addr.city || undefined,
        region: addr.region || undefined,
        postal_code: addr.postal_code || undefined,
        country: addr.country || undefined,
        geo_location: addr.geo_location ?? null,
        notes: form.notes || undefined,
        created_by: userId() || undefined,
        date_created: new Date().toISOString(),
        details,
        pallets_summary: palletGroups.some((g) => g.pallets > 0)
          ? Object.fromEntries(
              palletGroups
                .filter((g) => g.pallets > 0)
                .map((g) => [
                  g.type_code || `pallet_${g.pallet_asset_type_id}`,
                  g.pallets,
                ]),
            )
          : undefined,
      };

      await createorder(payload);

      const summary = form.lines
        .filter((l) => l.asset_id)
        .map((l) => {
          const palStr =
            l.has_pallet && Number(l.pallets)
              ? ` (${l.pallets} pallet${Number(l.pallets) !== 1 ? "s" : ""})`
              : "";
          return `${l.product || "Asset"} ${l.qty}${palStr}`;
        })
        .join("; ");
      showToast?.(`Order placed: ${summary}`, "s");
      setPage("orders");
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="fb mb16">
        <div>
          <div className="ph-title">Create New Order</div>
          <div className="hdr-crumb">
            Orders / New · Step {step + 1} of {STEPS.length}
          </div>
        </div>
        <button className="back-btn" onClick={() => setPage("orders")}>
          Cancel
        </button>
      </div>

      <div className="panel" style={{ padding: "22px 24px" }}>
        <div style={{ minHeight: 260 }}>
          <div style={{ display: step === 0 ? "block" : "none" }}>
            <StepOrderBuilder
              form={form}
              setForm={setForm}
              customers={customers}
              setCustomers={setCustomers}
              custLoading={custLoading}
              setCustLoading={setCustLoading}
              custError={custError}
              setCustError={setCustError}
              suppliers={suppliers}
              setSuppliers={setSuppliers}
              suppLoading={suppLoading}
              setSuppLoading={setSuppLoading}
              suppError={suppError}
              setSuppError={setSuppError}
              warehouses={warehouses}
              setWarehouses={setWarehouses}
              whLoading={whLoading}
              setWhLoading={setWhLoading}
              whError={whError}
              setWhError={setWhError}
              assets={assets}
              setAssets={setAssets}
              assetsLoading={assetsLoading}
              setAssetsLoading={setAssetsLoading}
              assetsError={assetsError}
              setAssetsError={setAssetsError}
              assetsFetchId={assetsFetchId}
            />
          </div>
          {step === 1 && (
            <StepReviewConfirm
              form={form}
              submitting={submitting}
              submitError={submitError}
              palletGroups={palletGroups}
            />
          )}
        </div>

        <div
          className="fc"
          style={{
            justifyContent: "space-between",
            marginTop: 15,
            paddingTop: 18,
            borderTop: "1px solid var(--border)",
          }}
        >
          <div
            className="fe"
            style={{
              marginRight: 10,
              padding: "6px 12px",
              borderRadius: 6,
              transition: "all .15s",
            }}
          >
            <button
              className="btn"
              onClick={handleBack}
              style={{ opacity: isFirst || submitting ? 0.6 : 1 }}
            >
              {isFirst ? "Back to Orders" : "Back"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            {STEPS.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === step ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background:
                    i < step
                      ? "var(--success)"
                      : i === step
                        ? "var(--flame)"
                        : "var(--bright)",
                  transition: "all .2s",
                }}
              />
            ))}
          </div>
          {isLast ? (
            <button
              className="btn btn-p"
              onClick={handleConfirm}
              disabled={submitting}
              style={{ opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? "Submitting…" : "✓ Submit"}
            </button>
          ) : (
            <button className="btn btn-p" onClick={handleNext}>
              Next: {STEPS[step + 1].label} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateOrderPage;